/**
 * Chip
 * Classification: mui-overrides (adds `subtle` + `dot` variants, extended color axis)
 * MUI base: https://mui.com/material-ui/react-chip/
 *
 * The single compact-label primitive. Callers pick a `purpose` from meaning and
 * the component derives the appearance, the rendered semantics and the keyboard
 * contract from it:
 *
 *   static      severity | status | category | metric | lifecycle
 *   interactive filter   | select | entity   | overflow | picker
 *
 * The older `variant` + `color` API still works and is unchanged, but it is
 * deprecated: four appearances crossed with ten colors is forty combinations of
 * which roughly eight are correct usage, and nothing in the types said which.
 * `purpose` collapses that to eight legal states.
 *
 * The prop is `purpose` rather than `role` because `role` is a DOM/ARIA
 * attribute already present on `ChipProps` via `HTMLAttributes`; reusing the
 * name would collide with it in the type system and read ambiguously next to a
 * genuine `role="button"`.
 */
import MuiChip, { type ChipProps as MuiChipProps } from '@mui/material/Chip';
import Box from '@mui/material/Box';
import { darken, type Theme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { type SeverityKey } from '@/tokens/colors';
import { compactGlyphScale, fontFamily } from '@/tokens/typography';
import { ENTITY_KIND_LABEL, type EntityKind, type EntityVerdict } from './entity';
import { pickerLabel, type PickerSummary } from './picker';

/** What the chip means. Chosen from intent, never from appearance. */
export type ChipPurpose =
  | 'severity'
  | 'status'
  | 'category'
  | 'metric'
  | 'lifecycle'
  | 'filter'
  | 'select'
  | 'entity'
  | 'overflow'
  | 'picker';

/** Chip size scale. `small` is dense; `medium` matches Button `small`. */
export type ChipSize = 'small' | 'medium' | 'large' | 'extraLarge';

/**
 * How mature a *feature* is. Deliberately not `status`: a workflow state
 * describes a record and the user may act on it, whereas a stage describes the
 * product surface itself and changes only when engineering ships something.
 * BigCommerce and GitHub both keep these apart for the same reason.
 *
 * Closed on purpose. A free-text version of this becomes "Coming soon",
 * "COMING SOON" and "Coming Soon!" in three screens.
 */
export type LifecycleStage = 'new' | 'beta' | 'alpha' | 'preview' | 'deprecated';

/**
 * Semantic tone for a workflow or health state. Deliberately separate from the
 * severity scale: an alert's severity and its workflow state are different
 * dimensions and must not share a color vocabulary. Matches the `tone` values
 * on `TableStatusCell` so a status reads the same in and out of a table.
 */
export type ChipStatusTone = 'active' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

/** Intelligence verdict on an entity. Uses the status palette, not severity. */
export type ChipEntityVerdict = EntityVerdict;

/**
 * Props every purpose accepts. `variant`, `color`, `label`, `onClick`,
 * `onDelete`, `clickable` and `deleteIcon` are withheld on purpose: appearance
 * and interaction are derived from `purpose` and must not be overridden, which
 * is what keeps a static label from being styled to look interactive.
 *
 * `onToggle` is dropped for a different reason: `HTMLAttributes` declares it as
 * the DOM toggle event for `<details>`, so leaving it in place would intersect
 * with the select purpose's own `onToggle` and make it uncallable.
 *
 * `size` stays available here because seven of the eight purposes honour it;
 * `entity` withholds it separately, for the reason given on `EntityChipProps`.
 */
type ChipPurposeBase = Omit<
  MuiChipProps,
  'variant' | 'color' | 'label' | 'onClick' | 'onDelete' | 'clickable' | 'deleteIcon' | 'onToggle'
>;

/** Severity or priority. Static. The label is derived from `level`. */
export type SeverityChipProps = ChipPurposeBase & {
  purpose: 'severity';
  level: SeverityKey;
  /** Overrides the canonical word. Only for a genuinely different vocabulary. */
  label?: string;
};

/** Workflow or health state. Static. */
export type StatusChipProps = ChipPurposeBase & {
  purpose: 'status';
  tone: ChipStatusTone;
  label: string;
};

/** Category or classification metadata. Static, always neutral. */
export type CategoryChipProps = ChipPurposeBase & {
  purpose: 'category';
  label: string;
};

/** A measured value such as a file size or a related-item count. Static. */
export type MetricChipProps = ChipPurposeBase & {
  purpose: 'metric';
  label: string;
};

/**
 * The release stage of a feature: `New`, `Beta`, `Deprecated`. Static, and the
 * word is derived from `stage` so the same feature cannot read as "Beta" in the
 * nav and "BETA" on its page.
 *
 * This marks the product surface, never a record. If you would let a user
 * filter a list by it, it is data and belongs in `category` -- "Built-in" on a
 * workflow row is a property of that workflow, not a release stage.
 */
export type LifecycleChipProps = ChipPurposeBase & {
  purpose: 'lifecycle';
  stage: LifecycleStage;
  /** Overrides the canonical word, for a product with its own vocabulary. */
  label?: string;
};

/** An applied filter the user can remove. `onRemove` is required. */
export type FilterChipProps = ChipPurposeBase & {
  purpose: 'filter';
  label: string;
  /** Rendered as `field: label` so the criterion survives out of context. */
  field?: string;
  onRemove: () => void;
};

/**
 * A toggleable option. `selected` and `onToggle` are both required.
 *
 * Each chip is independent: toggling it does not change its neighbours. That
 * is the standalone on/off — the same chip is pressed or not. A mutually
 * exclusive group is parent state, not a chip mode: keep one selected value
 * and pass `selected={value === option}` so clicking one switches the group
 * to that option.
 */
export type SelectChipProps = ChipPurposeBase & {
  purpose: 'select';
  label: string;
  selected: boolean;
  onToggle: (next: boolean) => void;
};

/**
 * An entity value inside prose or a dense cell. Opens a detail menu.
 *
 * `size` is withheld here and nowhere else. An entity chip derives its type
 * from the line box it sits in, so a size from the chip scale has nothing to
 * act on -- accepting the prop would compile and then do nothing, which is the
 * failure this API exists to prevent. To make one bigger or smaller, change the
 * surrounding `Typography`.
 */
export type EntityChipProps = Omit<ChipPurposeBase, 'size'> & {
  purpose: 'entity';
  /** What the value is. Spoken in the accessible name and gates defanging. */
  kind: EntityKind;
  /** Canonical, full value. This is what a copy action must yield. */
  value: string;
  /** Shortened text for display. Defaults to `value`. */
  display?: string;
  /** Omit when the value was never resolved: an unresolved chip carries no tint. */
  verdict?: EntityVerdict;
  onOpen?: (event: React.MouseEvent<HTMLElement>) => void;
};

/** Discloses the remainder of a capped group. */
export type OverflowChipProps = ChipPurposeBase & {
  purpose: 'overflow';
  count: number;
  onDisclose: () => void;
  expanded?: boolean;
};

/**
 * The trigger for a multi-select value picker: the field it filters, what is
 * currently selected, and a chevron. This is the one chip that both carries a
 * value and changes it, and the exception is narrow -- it changes *which values
 * are selected*, never the record underneath. A chip that fires a verb is still
 * a Button.
 *
 * Its two targets line up with the two things a user wants from an applied
 * filter, and neither duplicates the other: the body opens the panel, and the X
 * removes the filter just as it does on `purpose="filter"`. Emptying the
 * selection without losing the field is `Reset`, inside the panel.
 *
 * It renders the trigger only. The panel of options is `FilterMenu`, which owns
 * this chip and passes `expanded`, exactly as `EntityMenu` pairs with the
 * entity purpose. Reach for `FilterMenu`; a bare picker chip is for the rare
 * case where you are supplying your own surface.
 *
 * `values` are the labels a user reads, already resolved from option values. A
 * chip showing `sev_1` instead of `Critical` has failed.
 */
export type PickerChipProps = ChipPurposeBase & {
  purpose: 'picker';
  /** The filter's name, e.g. `Severity`. Shown even when nothing is selected. */
  field: string;
  values: string[];
  /** Whether the panel is open. Drives the chevron and `aria-expanded`. */
  expanded: boolean;
  onOpen: (event: React.MouseEvent<HTMLElement>) => void;
  /**
   * Removes the whole filter, exactly as `onRemove` does on `purpose="filter"`.
   * The X is the same glyph in the same corner on both, so it has to mean the
   * same thing: the criterion leaves the bar. Clearing the *selection* while
   * keeping the field is the panel's `Reset`, not this.
   *
   * The caller owns the removal, since a chip cannot take itself out of its
   * parent's list of active fields. Omit it for a bar of fixed fields that are
   * always present.
   */
  onRemove?: () => void;
  /** Defaults to `value`. Use `count` when the values are too long to show. */
  summary?: PickerSummary;
  /** The id of the panel this chip opens, for `aria-controls`. */
  panelId?: string;
};

/**
 * The pre-`purpose` API. Still supported so consumers can upgrade the package
 * without touching code, and warned about once per combination in development.
 */
export type LegacyChipProps = MuiChipProps & { purpose?: undefined };

export type ChipProps =
  | SeverityChipProps
  | StatusChipProps
  | CategoryChipProps
  | MetricChipProps
  | LifecycleChipProps
  | FilterChipProps
  | SelectChipProps
  | EntityChipProps
  | OverflowChipProps
  | PickerChipProps
  | LegacyChipProps;

/** Canonical severity words. Derived rather than passed so `level` and `label` cannot disagree. */
const SEVERITY_LABEL: Record<SeverityKey, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Info',
  untriaged: 'Untriaged',
};

const SEVERITY_COLOR = {
  critical: 'critical',
  high: 'high',
  medium: 'medium',
  low: 'low',
  info: 'info',
  untriaged: 'neutral',
} as const satisfies Record<SeverityKey, MuiChipProps['color']>;

const STATUS_COLOR = {
  active: 'success',
  success: 'success',
  warning: 'warning',
  error: 'error',
  info: 'info',
  neutral: 'neutral',
} as const satisfies Record<ChipStatusTone, MuiChipProps['color']>;

/** Canonical stage words. Derived so one feature cannot be "Beta" and "BETA". */
const LIFECYCLE_LABEL: Record<LifecycleStage, string> = {
  new: 'New',
  beta: 'Beta',
  alpha: 'Alpha',
  preview: 'Preview',
  deprecated: 'Deprecated',
};

const VERDICT_COLOR = {
  malicious: 'error',
  suspicious: 'warning',
  benign: 'success',
  unknown: 'neutral',
} as const satisfies Record<ChipEntityVerdict, MuiChipProps['color']>;

/**
 * An entity chip sits inside a sentence, so it takes its height and size from
 * the surrounding line box instead of the chip size scale. A chip with its own
 * vertical padding pushes its line further apart than its neighbours, which
 * reads as a rendering bug rather than as emphasis. Nothing here may use a
 * fixed size: the whole point is that the chip is subordinate to the text it
 * sits in.
 *
 * `letterSpacing` is reset because the theme tracks chip text for legibility at
 * small sizes, which fights a monospace value an analyst is reading character
 * by character before copying it.
 */
const ENTITY_SX = {
  height: 'auto',
  fontFamily: fontFamily.mono,
  // `em`, not `rem` or a fixed size, so the chip tracks whatever type it is
  // dropped into -- body copy, a table cell, a caption. The 0.875 factor is
  // the correction for the font swap: a monospace face at the same nominal
  // size reads visibly larger than the proportional body face beside it,
  // because its glyphs carry a taller x-height and a wider fixed advance.
  // This matches the inline `code` scale, which is the same problem solved
  // the same way, and keeps an entity chip and a code span the same size
  // when they sit in one sentence.
  fontSize: '0.875em',
  letterSpacing: 'normal',
  verticalAlign: 'baseline',
  '& .MuiChip-label': { px: 0.75, py: 0, lineHeight: 'inherit' },
} as const;

/**
 * A `field: value` label is several things in a row rather than a string -- for
 * a picker, the field and its selection, the `+N` counter, then the chevron --
 * so it is a flex line. `inline-flex` keeps it from establishing a block box
 * inside MUI's label span, which would break the chip's vertical centring.
 */
const TWO_TONE_LABEL_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
} as const;

/**
 * The field name is the constant and the value is what changes, so the field
 * keeps the chip's own muted ink and the value is promoted to the primary one.
 * Without the split, the eye has to read the whole pill to find the one word
 * that differs between two chips in the same bar.
 *
 * Shared by `purpose="filter"` and `purpose="picker"`, which sit side by side in
 * the same bar and name their field the same way. A criterion the user can only
 * remove and one they can also edit should differ by the chevron, not by how
 * their text is weighted.
 */
const FIELD_NAME_SX = { color: 'text.secondary' } as const;
const FIELD_VALUE_SX = { color: 'text.primary', fontWeight: 500 } as const;

/**
 * The `+N` counter. An outlined pill in the brand hue, drawn inside the label
 * rather than as a `Badge`: a Badge anchors a count *to* an element from
 * outside it, which would float this over the chip's border and clip against
 * its neighbour in a wrapping filter bar.
 *
 * The brand ink is darkened in light mode for the same reason the lifecycle
 * chip darkens it -- the palette's turquoise is tuned for a dark surface and
 * misses AA against a light one.
 */
const PICKER_COUNTER_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 999,
  border: '1px solid',
  borderColor: (theme: Theme) =>
    theme.palette.mode === 'light' ? darken(theme.palette.primary.main, 0.1) : theme.palette.primary.main,
  color: (theme: Theme) =>
    theme.palette.mode === 'light' ? darken(theme.palette.primary.main, 0.1) : theme.palette.primary.main,
  fontSize: '0.75rem',
  fontWeight: 500,
  lineHeight: 1,
  px: 0.625,
  py: '3px',
  // The counter must not stretch the pill it sits in.
  flexShrink: 0,
} as const;

/**
 * The chevron points down when closed and flips when open. Getting this wrong
 * is a real bug rather than a polish issue: a chevron that never moves stops
 * being a state indicator and becomes decoration.
 */
const PICKER_CHEVRON_SX = {
  // The same ratio the theme gives the leading and delete icons. It was 1.125rem
  // (a flat 18px), which made the chevron a third size on a chip that already
  // had two.
  fontSize: compactGlyphScale,
  color: 'text.secondary',
  flexShrink: 0,
  transition: 'transform 150ms ease',
} as const;

/**
 * Props owned by one or more purposes. Everything else a caller passes -- `sx`,
 * `size`, `icon`, `disabled`, `data-*`, event handlers -- is forwarded to MUI
 * untouched. Keeping this as one list means a new purpose adds its props here
 * rather than adding another destructure to every branch below.
 */
const PURPOSE_OWNED_PROPS = new Set<string>([
  'purpose',
  'level',
  'tone',
  'stage',
  'label',
  'field',
  'onRemove',
  'selected',
  'onToggle',
  'value',
  'display',
  'verdict',
  'kind',
  'onOpen',
  'count',
  'onDisclose',
  'expanded',
  'values',
  'summary',
  'panelId',
]);

/** The caller's props minus everything a purpose derives, ready to spread onto MUI. */
function forwarded(props: ChipProps): MuiChipProps {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(props)) {
    if (!PURPOSE_OWNED_PROPS.has(key)) out[key] = (props as Record<string, unknown>)[key];
  }
  return out as MuiChipProps;
}

const warnedLegacy = new Set<string>();

function warnLegacyUsage(variant: unknown, color: unknown): void {
  if (process.env.NODE_ENV === 'production') return;
  const key = `${String(variant)}/${String(color)}`;
  if (warnedLegacy.has(key)) return;
  warnedLegacy.add(key);
  console.warn(
    `[Octopus UI] <Chip variant="${String(variant)}" color="${String(color)}" /> uses the ` +
      'deprecated appearance API. Pass a `purpose` instead (severity, status, category, metric, ' +
      'lifecycle, filter, select, entity, overflow, picker) and the appearance is derived for ' +
      'you. See Guidelines > Choosing > Compact Labels.',
  );
}

/**
 * The compact-label primitive.
 *
 * @example
 * <Chip purpose="severity" level="critical" />
 * <Chip purpose="status" tone="warning" label="Investigating" />
 * <Chip purpose="filter" field="Severity" label="Critical" onRemove={clear} />
 * <Chip purpose="entity" kind="ip" value="203.0.113.47" verdict="malicious" />
 *
 * @example Prefer FilterMenu, which owns this chip and its panel.
 * <Chip purpose="picker" field="Severity" values={['Critical', 'High']}
 *       expanded={open} onOpen={openPanel} onRemove={dropField} />
 */
export function Chip(props: ChipProps) {
  const rest = forwarded(props);

  switch (props.purpose) {
    case 'severity':
      return (
        <MuiChip
          {...rest}
          variant="outlined"
          color={SEVERITY_COLOR[props.level]}
          label={props.label ?? SEVERITY_LABEL[props.level]}
        />
      );

    case 'status':
      return (
        <MuiChip {...rest} variant="dot" color={STATUS_COLOR[props.tone]} label={props.label} />
      );

    // The three neutral pills share the `field` chrome: an input-coloured fill
    // in a quiet outline, so a bar of them reads as one row of controls rather
    // than as three kinds of object. Colour stays reserved for severity.
    case 'category':
    case 'metric':
      return <MuiChip {...rest} variant="field" label={props.label} />;

    case 'lifecycle':
      // The one place the brand accent is spent on a chip, and it is spent on
      // the outline rather than a fill: an outlined pill in a hue no severity
      // level uses is legible as "not one of the data pills" without adding a
      // sixth colour to the scale severity owns. All five stages share it --
      // the colour says "release marker", the word says which.
      return (
        <MuiChip
          {...rest}
          variant="outlined"
          color="primary"
          label={props.label ?? LIFECYCLE_LABEL[props.stage]}
        />
      );

    case 'filter': {
      const text = props.field ? `${props.field}: ${props.label}` : props.label;
      return (
        <MuiChip
          {...rest}
          variant="field"
          // Two-tone, matching `purpose="picker"`: the field name muted, the
          // value promoted. `text` above stays the flat string for the
          // accessible name, so splitting the visual label costs nothing to a
          // screen reader. Without a `field` the whole label *is* the value.
          label={
            props.field ? (
              <Box component="span" sx={TWO_TONE_LABEL_SX}>
                <Box component="span" sx={FIELD_NAME_SX}>{`${props.field}:`}</Box>
                <Box component="span" sx={FIELD_VALUE_SX}>
                  {props.label}
                </Box>
              </Box>
            ) : (
              <Box component="span" sx={FIELD_VALUE_SX}>
                {props.label}
              </Box>
            )
          }
          onDelete={props.onRemove}
          // MUI's delete icon is not itself a focusable button; removal by
          // keyboard is Backspace or Delete on the focused chip. Naming the
          // chip as a filter is what makes that target unambiguous.
          aria-label={`${text} filter`}
        />
      );
    }

    case 'select': {
      const { selected, onToggle } = props;
      return (
        <MuiChip
          {...rest}
          variant={selected ? 'filled' : 'subtle'}
          color={selected ? 'primary' : 'neutral'}
          label={props.label}
          onClick={() => onToggle(!selected)}
          aria-pressed={selected}
        />
      );
    }

    case 'entity': {
      const { value, display, verdict, kind, onOpen } = props;
      const { sx, ...entityRest } = rest;
      const name = `${ENTITY_KIND_LABEL[kind]} ${value}`;
      return (
        <MuiChip
          {...entityRest}
          // An entity chip lives inside a sentence, so it must be phrasing
          // content: MUI's default `div` inside a `<p>` is invalid markup and
          // the browser closes the paragraph early, splitting the sentence.
          component="span"
          // Not the chip's size -- `ENTITY_SX` overrides the height and font
          // size this would set. It is here so an `icon`, if one is passed,
          // gets the small metrics rather than metrics for a 28px chip.
          size="small"
          variant="subtle"
          color={verdict ? VERDICT_COLOR[verdict] : 'neutral'}
          label={display ?? value}
          onClick={onOpen}
          aria-haspopup={onOpen ? 'menu' : undefined}
          // The visible text may be truncated, so the accessible name carries
          // the full value plus the verdict a sighted user reads from the tint.
          aria-label={verdict ? `${name}, ${verdict}` : name}
          sx={[ENTITY_SX, ...(Array.isArray(sx) ? sx : [sx])]}
        />
      );
    }

    case 'overflow':
      return (
        <MuiChip
          {...rest}
          variant="field"
          label={`+${props.count} more`}
          onClick={props.onDisclose}
          aria-expanded={props.expanded ?? false}
        />
      );

    case 'picker': {
      const { field, values, expanded, onOpen, onRemove, summary, panelId } = props;
      const { detail, overflow, accessibleName } = pickerLabel(field, values, summary);
      return (
        <MuiChip
          {...rest}
          // The same field chrome as `purpose="filter"`, because a picker chip
          // sits in the same bar and is the same kind of thing: an applied
          // criterion. What marks it as operable is the chevron, which no static
          // chip has -- a brighter border would have made the two read as
          // different species of pill when only their affordances differ.
          variant="field"
          onClick={onOpen}
          // Not gated on the selection: removing an empty filter field is a
          // real thing to want, and a target that appears and disappears as the
          // user ticks boxes is a moving goalpost.
          onDelete={onRemove}
          // A dialog, not a menu: the panel holds a search field and an Apply
          // button, which a menu's role cannot legally contain.
          aria-haspopup="dialog"
          aria-expanded={expanded}
          // Only while open, because the panel does not exist when it is closed
          // and a dangling reference is worse than none.
          aria-controls={expanded ? panelId : undefined}
          // The visible text elides the rest of the selection behind `+N`, so
          // the name spells it out.
          aria-label={accessibleName}
          label={
            <Box component="span" sx={TWO_TONE_LABEL_SX}>
              <Box component="span" sx={FIELD_NAME_SX}>
                {detail === undefined ? field : `${field}:`}
              </Box>
              {detail !== undefined && (
                <Box component="span" sx={FIELD_VALUE_SX}>
                  {detail}
                </Box>
              )}
              {overflow > 0 && (
                <Box component="span" sx={PICKER_COUNTER_SX}>
                  {`+${overflow}`}
                </Box>
              )}
              <ExpandMoreIcon
                sx={[
                  PICKER_CHEVRON_SX,
                  { transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' },
                ]}
              />
            </Box>
          }
        />
      );
    }

    default:
      // The pre-`purpose` API. `label` is a legitimate MUI prop here, so this
      // branch forwards the caller's props as given rather than the stripped set.
      if (props.variant || props.color) warnLegacyUsage(props.variant, props.color);
      return <MuiChip {...props} />;
  }
}

export default Chip;
